// vg-binding: P7.D-148
// vg-binding: P4.D-182
//
// apps/vendor/src/features/orders/detail/DimInputModal.tsx
//
// Phase 7 Wave 6 Task 21 — dim-input confirm modal shown when the vendor
// clicks "Start Production".
//
// P1-18 fix — the Wave-6 variant collected dimensions but threw them away
// on confirm (transition only, no persist). Now: when the vendor fills the
// fields, we persist them via useAssizeItem (PUT
// /vendor/orders/:orderId/items/:itemId/packaging-dimensions — same
// endpoint as AssizeModal) and only then run onConfirm (the
// in_production transition). If the persist fails, the error shows in
// the modal and NO transition fires. Leaving all four fields empty keeps
// the confirm-only path (the BE dim gate has `dim_satisfied=true` by
// default, dims are optional at this step).

import { useEffect, useState } from 'react';

import { Button, Input } from '@printway/ui';

import { useTranslation } from '../../../lib/i18n/index.js';
import { useAssizeItem, type OrdersApiError } from '../api.js';

interface Props {
  open: boolean;
  orderId: string;
  itemId: string;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}

export function DimInputModal({
  open,
  orderId,
  itemId,
  onCancel,
  onConfirm,
}: Props): JSX.Element | null {
  const { t } = useTranslation('orders');
  const assize = useAssizeItem();
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Clear stale error when the modal (re)opens.
  useEffect(() => {
    if (open) setErrorMsg(null);
  }, [open]);

  if (!open) return null;

  async function handleConfirm(): Promise<void> {
    setErrorMsg(null);

    const allEmpty =
      length.trim() === '' &&
      width.trim() === '' &&
      height.trim() === '' &&
      weight.trim() === '';

    if (!allEmpty) {
      const l = Number.parseFloat(length);
      const w = Number.parseFloat(width);
      const h = Number.parseFloat(height);
      const wg = Number.parseInt(weight, 10);
      if (
        !Number.isFinite(l) || l <= 0 ||
        !Number.isFinite(w) || w <= 0 ||
        !Number.isFinite(h) || h <= 0 ||
        !Number.isInteger(wg) || wg <= 0
      ) {
        setErrorMsg(t('assize.invalid'));
        return;
      }
      try {
        await assize.mutateAsync({ orderId, itemId, dims: { l, w, h, weightGrams: wg } });
      } catch (err) {
        const e = err as OrdersApiError;
        setErrorMsg(e.userMessage ?? e.message ?? t('assize.saveFailed'));
        return; // persist failed → NO transition
      }
    }

    await onConfirm();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="dim-input-modal"
      /* dark-ok: modal scrim, translucent black reads correctly in both modes */
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md rounded-xl border border-pw-border bg-pw-bg p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-pw-text">
          {t('dimModal.title')}
        </h2>
        <p className="mt-2 text-sm text-pw-text-muted">
          {t('dimModal.description')}
        </p>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t('dimModal.labelLength')} value={length} onChange={setLength} />
          <Field label={t('dimModal.labelWidth')} value={width} onChange={setWidth} />
          <Field label={t('dimModal.labelHeight')} value={height} onChange={setHeight} />
          <Field label={t('dimModal.labelWeight')} value={weight} onChange={setWeight} />
        </div>

        {errorMsg ? (
          <p
            data-testid="dim-modal-error"
            className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {errorMsg}
          </p>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="dim-modal-cancel"
            onClick={onCancel}
          >
            {t('dimModal.cancel')}
          </Button>
          <Button
            type="button"
            data-testid="dim-modal-confirm"
            disabled={assize.isPending}
            onClick={() => void handleConfirm()}
          >
            {assize.isPending ? t('assize.saving') : t('dimModal.confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-pw-text-muted">{label}</span>
      <Input
        type="number"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9"
      />
    </label>
  );
}

export default DimInputModal;
